import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Loader2, CheckCircle2, Calendar, Users, Shield, Phone, Mail, Plus, X, Clock, CalendarDays, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import siteConfig from '../config/siteConfig';
import { cn } from '../lib/utils';

// Generate time options in 30-minute intervals
const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const h = hour.toString().padStart(2, '0');
      const m = minute.toString().padStart(2, '0');
      const time = `${h}:${m}`;
      const displayTime = `${hour === 0 ? 12 : hour > 12 ? hour - 12 : hour}:${m} ${hour < 12 ? 'AM' : 'PM'}`;
      options.push({ value: time, label: displayTime });
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

// Validate time slots don't overlap
const validateTimeSlots = (slots) => {
  if (slots.length <= 1) return { valid: true, message: '' };
  
  const sortedSlots = [...slots].sort((a, b) => a.start.localeCompare(b.start));
  
  for (let i = 0; i < sortedSlots.length - 1; i++) {
    const current = sortedSlots[i];
    const next = sortedSlots[i + 1];
    
    if (current.end > next.start) {
      return { 
        valid: false, 
        message: `Time slots overlap: ${current.start}-${current.end} and ${next.start}-${next.end}` 
      };
    }
  }
  
  return { valid: true, message: '' };
};

// Validate start time is before end time
const validateSlotTimes = (start, end) => {
  if (!start || !end) return true;
  return start < end;
};

export default function EventQuotePage() {
  const navigate = useNavigate();
  const { company, contact, logo, eventQuote, eventTypes, coverageLevels, attendeeOptions } = siteConfig;
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  
  const [formData, setFormData] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    event_location: '',
    expected_attendees: '',
    event_type: '',
    coverage_level: '',
    additional_notes: '',
  });
  
  // Multi-date/time state
  const [eventDateEntries, setEventDateEntries] = useState([
    { 
      id: Date.now(), 
      date: '', 
      time_slots: [{ id: Date.now(), start: '', end: '' }] 
    }
  ]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error when field is updated
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Date entry handlers
  const addDateEntry = () => {
    setEventDateEntries(prev => [
      ...prev,
      { 
        id: Date.now(), 
        date: '', 
        time_slots: [{ id: Date.now(), start: '', end: '' }] 
      }
    ]);
  };

  const removeDateEntry = (dateId) => {
    if (eventDateEntries.length === 1) {
      toast.error('At least one date is required');
      return;
    }
    setEventDateEntries(prev => prev.filter(entry => entry.id !== dateId));
  };

  const updateDateEntry = (dateId, date) => {
    setEventDateEntries(prev => prev.map(entry => 
      entry.id === dateId ? { ...entry, date } : entry
    ));
    setValidationErrors(prev => ({ ...prev, dates: null }));
  };

  // Time slot handlers
  const addTimeSlot = (dateId) => {
    setEventDateEntries(prev => prev.map(entry => {
      if (entry.id === dateId) {
        return {
          ...entry,
          time_slots: [
            ...entry.time_slots,
            { id: Date.now(), start: '', end: '' }
          ]
        };
      }
      return entry;
    }));
  };

  const removeTimeSlot = (dateId, slotId) => {
    setEventDateEntries(prev => prev.map(entry => {
      if (entry.id === dateId) {
        if (entry.time_slots.length === 1) {
          toast.error('At least one time slot is required per date');
          return entry;
        }
        return {
          ...entry,
          time_slots: entry.time_slots.filter(slot => slot.id !== slotId)
        };
      }
      return entry;
    }));
  };

  const updateTimeSlot = (dateId, slotId, field, value) => {
    setEventDateEntries(prev => prev.map(entry => {
      if (entry.id === dateId) {
        return {
          ...entry,
          time_slots: entry.time_slots.map(slot => 
            slot.id === slotId ? { ...slot, [field]: value } : slot
          )
        };
      }
      return entry;
    }));
    setValidationErrors(prev => ({ ...prev, timeSlots: null }));
  };

  const validateForm = () => {
    const errors = {};
    
    // Required fields
    const required = ['business_name', 'contact_name', 'email', 'phone', 'event_location', 'expected_attendees', 'event_type', 'coverage_level'];
    required.forEach(field => {
      if (!formData[field]) {
        errors[field] = 'This field is required';
      }
    });
    
    // Validate dates
    const hasValidDate = eventDateEntries.some(entry => entry.date);
    if (!hasValidDate) {
      errors.dates = 'At least one event date is required';
    }
    
    // Validate time slots
    let hasValidTimeSlot = false;
    let hasTimeError = false;
    
    eventDateEntries.forEach((entry, dateIndex) => {
      if (entry.date) {
        const slotValidation = validateTimeSlots(entry.time_slots.filter(s => s.start && s.end));
        if (!slotValidation.valid) {
          errors.timeSlots = `Date ${dateIndex + 1}: ${slotValidation.message}`;
          hasTimeError = true;
        }
        
        entry.time_slots.forEach((slot, slotIndex) => {
          if (slot.start && slot.end) {
            if (!validateSlotTimes(slot.start, slot.end)) {
              errors.timeSlots = `Date ${dateIndex + 1}, Slot ${slotIndex + 1}: End time must be after start time`;
              hasTimeError = true;
            } else {
              hasValidTimeSlot = true;
            }
          }
        });
      }
    });
    
    if (!hasValidTimeSlot && !hasTimeError) {
      errors.timeSlots = 'At least one complete time slot (start and end) is required';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }

    // Prepare data for submission
    const eventDateEntriesFormatted = eventDateEntries
      .filter(entry => entry.date)
      .map(entry => ({
        date: entry.date,
        time_slots: entry.time_slots
          .filter(slot => slot.start && slot.end)
          .map(slot => ({ start: slot.start, end: slot.end }))
      }))
      .filter(entry => entry.time_slots.length > 0);

    const submitData = {
      ...formData,
      event_date_entries: eventDateEntriesFormatted,
    };

    setLoading(true);
    try {
      const response = await api.post('/event-quotes', submitData);
      setReferenceNumber(response.data.reference_number);
      setSubmitted(true);
      toast.success('Quote request submitted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit quote request');
    }
    setLoading(false);
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <Card className="w-full max-w-lg text-center border-0 shadow-xl">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2 text-slate-900">{eventQuote.successTitle}</h2>
            <p className="text-slate-600 mb-6">{eventQuote.successMessage}</p>
            <div className="bg-slate-100 p-4 rounded-xl mb-6">
              <p className="text-sm text-slate-500 mb-1">Your Reference Number</p>
              <p className="text-2xl font-mono font-bold text-slate-900">{referenceNumber}</p>
            </div>
            <p className="text-sm text-slate-500 mb-6">Please save this reference number for your records.</p>
            <Button onClick={() => navigate('/login')} className="w-full bg-primary hover:bg-primary/90 text-white">
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4" data-testid="event-quote-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center text-slate-500 hover:text-slate-900 mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <img src={logo.url} alt={logo.alt} className="w-20 h-20 mx-auto mb-4 rounded-xl object-contain shadow-lg" />
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">{eventQuote.title}</h1>
          <p className="text-slate-600 mt-2">{eventQuote.subtitle}</p>
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {eventQuote.features.map((feature, i) => {
            const icons = [Shield, Users, Calendar];
            const Icon = icons[i] || Shield;
            return (
              <Card key={i} className="text-center p-4 border-0 shadow-md bg-white/80 backdrop-blur">
                <Icon className="w-8 h-8 mx-auto mb-2 text-primary" />
                <h3 className="font-semibold text-sm text-slate-900">{feature.title}</h3>
                <p className="text-xs text-slate-500">{feature.description}</p>
              </Card>
            );
          })}
        </div>

        {/* Premium CTA Banner */}
        <Card className="mb-8 overflow-hidden border-0 shadow-xl bg-gradient-to-r from-primary to-primary/80">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-2">{eventQuote.heroTitle}</h2>
            <p className="text-white/80 mb-6">{eventQuote.heroSubtitle}</p>
            <Button 
              size="lg"
              onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
              className="bg-white text-primary hover:bg-slate-100 font-semibold px-10 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 text-lg"
            >
              {eventQuote.heroButton}
            </Button>
          </CardContent>
        </Card>

        {/* Quote Form */}
        <Card id="quote-form" className="border-0 shadow-xl bg-white">
          <CardHeader>
            <CardTitle className="text-slate-900">{eventQuote.formTitle}</CardTitle>
            <CardDescription>{eventQuote.formSubtitle}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Business Information */}
              <div>
                <h3 className="font-semibold mb-4 flex items-center gap-2 text-slate-900">
                  <Users className="w-4 h-4 text-primary" />
                  Business Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="business_name">Business/Organization Name *</Label>
                    <Input 
                      id="business_name" 
                      value={formData.business_name} 
                      onChange={(e) => handleChange('business_name', e.target.value)} 
                      placeholder="Company Name" 
                      className={cn("bg-white", validationErrors.business_name && "border-red-300")}
                    />
                    {validationErrors.business_name && <p className="text-xs text-red-500">{validationErrors.business_name}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_name">Contact Person *</Label>
                    <Input 
                      id="contact_name" 
                      value={formData.contact_name} 
                      onChange={(e) => handleChange('contact_name', e.target.value)} 
                      placeholder="Your Name" 
                      className={cn("bg-white", validationErrors.contact_name && "border-red-300")}
                    />
                    {validationErrors.contact_name && <p className="text-xs text-red-500">{validationErrors.contact_name}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input 
                      id="email" 
                      type="email" 
                      value={formData.email} 
                      onChange={(e) => handleChange('email', e.target.value)} 
                      placeholder="you@company.com" 
                      className={cn("bg-white", validationErrors.email && "border-red-300")}
                    />
                    {validationErrors.email && <p className="text-xs text-red-500">{validationErrors.email}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input 
                      id="phone" 
                      type="tel" 
                      value={formData.phone} 
                      onChange={(e) => handleChange('phone', e.target.value)} 
                      placeholder={contact.phone} 
                      className={cn("bg-white", validationErrors.phone && "border-red-300")}
                    />
                    {validationErrors.phone && <p className="text-xs text-red-500">{validationErrors.phone}</p>}
                  </div>
                </div>
              </div>

              {/* Event Details */}
              <div>
                <h3 className="font-semibold mb-4 flex items-center gap-2 text-slate-900">
                  <CalendarDays className="w-4 h-4 text-primary" />
                  Event Details
                </h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="event_location">Event Location *</Label>
                    <Input 
                      id="event_location" 
                      value={formData.event_location} 
                      onChange={(e) => handleChange('event_location', e.target.value)} 
                      placeholder="Venue name, address, city" 
                      className={cn("bg-white", validationErrors.event_location && "border-red-300")}
                    />
                    {validationErrors.event_location && <p className="text-xs text-red-500">{validationErrors.event_location}</p>}
                  </div>

                  {/* Multi-Date/Time Selection */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-semibold">Event Dates & Times *</Label>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        onClick={addDateEntry}
                        className="gap-1 rounded-full"
                      >
                        <Plus className="h-4 w-4" />
                        Add Date
                      </Button>
                    </div>
                    
                    {validationErrors.dates && (
                      <Alert className="bg-red-50 border-red-200">
                        <AlertCircle className="h-4 w-4 text-red-500" />
                        <AlertDescription className="text-red-600">{validationErrors.dates}</AlertDescription>
                      </Alert>
                    )}
                    
                    {validationErrors.timeSlots && (
                      <Alert className="bg-red-50 border-red-200">
                        <AlertCircle className="h-4 w-4 text-red-500" />
                        <AlertDescription className="text-red-600">{validationErrors.timeSlots}</AlertDescription>
                      </Alert>
                    )}

                    <div className="space-y-4">
                      {eventDateEntries.map((dateEntry, dateIndex) => (
                        <div 
                          key={dateEntry.id} 
                          className="p-4 rounded-xl bg-slate-50 border border-slate-200"
                          data-testid={`date-entry-${dateIndex}`}
                        >
                          {/* Date Header */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-primary/10 text-primary border-0">
                                Day {dateIndex + 1}
                              </Badge>
                              {dateEntry.date && (
                                <span className="text-sm text-slate-600">
                                  {formatDisplayDate(dateEntry.date)}
                                </span>
                              )}
                            </div>
                            {eventDateEntries.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeDateEntry(dateEntry.id)}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>

                          {/* Date Input */}
                          <div className="mb-4">
                            <Label className="text-sm text-slate-600">Select Date</Label>
                            <Input
                              type="date"
                              value={dateEntry.date}
                              onChange={(e) => updateDateEntry(dateEntry.id, e.target.value)}
                              className="mt-1 bg-white"
                              min={new Date().toISOString().split('T')[0]}
                              data-testid={`date-input-${dateIndex}`}
                            />
                          </div>

                          {/* Time Slots */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label className="text-sm text-slate-600 flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                Time Slots
                              </Label>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => addTimeSlot(dateEntry.id)}
                                className="h-7 text-xs gap-1 text-primary hover:text-primary/80"
                              >
                                <Plus className="h-3 w-3" />
                                Add Time Slot
                              </Button>
                            </div>

                            {dateEntry.time_slots.map((slot, slotIndex) => (
                              <div 
                                key={slot.id} 
                                className="flex items-center gap-2 p-2 bg-white rounded-lg border border-slate-100"
                                data-testid={`time-slot-${dateIndex}-${slotIndex}`}
                              >
                                <div className="flex-1 grid grid-cols-2 gap-2">
                                  <div>
                                    <Label className="text-xs text-slate-500">Start Time</Label>
                                    <Select
                                      value={slot.start}
                                      onValueChange={(v) => updateTimeSlot(dateEntry.id, slot.id, 'start', v)}
                                    >
                                      <SelectTrigger className="h-9 bg-white text-sm">
                                        <SelectValue placeholder="Start" />
                                      </SelectTrigger>
                                      <SelectContent className="max-h-60">
                                        {TIME_OPTIONS.map(opt => (
                                          <SelectItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <Label className="text-xs text-slate-500">End Time</Label>
                                    <Select
                                      value={slot.end}
                                      onValueChange={(v) => updateTimeSlot(dateEntry.id, slot.id, 'end', v)}
                                    >
                                      <SelectTrigger className="h-9 bg-white text-sm">
                                        <SelectValue placeholder="End" />
                                      </SelectTrigger>
                                      <SelectContent className="max-h-60">
                                        {TIME_OPTIONS.map(opt => (
                                          <SelectItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                                {dateEntry.time_slots.length > 1 && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeTimeSlot(dateEntry.id, slot.id)}
                                    className="h-8 w-8 p-0 text-slate-400 hover:text-red-500 hover:bg-red-50 self-end"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="expected_attendees">Expected Attendees *</Label>
                      <Select value={formData.expected_attendees} onValueChange={(v) => handleChange('expected_attendees', v)}>
                        <SelectTrigger className={cn("bg-white", validationErrors.expected_attendees && "border-red-300")}>
                          <SelectValue placeholder="Select expected attendance" />
                        </SelectTrigger>
                        <SelectContent>
                          {attendeeOptions.map(opt => (<SelectItem key={opt} value={opt}>{opt}</SelectItem>))}
                        </SelectContent>
                      </Select>
                      {validationErrors.expected_attendees && <p className="text-xs text-red-500">{validationErrors.expected_attendees}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="event_type">Event Type *</Label>
                      <Select value={formData.event_type} onValueChange={(v) => handleChange('event_type', v)}>
                        <SelectTrigger className={cn("bg-white", validationErrors.event_type && "border-red-300")}>
                          <SelectValue placeholder="Select event type" />
                        </SelectTrigger>
                        <SelectContent>
                          {eventTypes.map(type => (<SelectItem key={type} value={type}>{type}</SelectItem>))}
                        </SelectContent>
                      </Select>
                      {validationErrors.event_type && <p className="text-xs text-red-500">{validationErrors.event_type}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Coverage Requirements */}
              <div>
                <h3 className="font-semibold mb-4 flex items-center gap-2 text-slate-900">
                  <Shield className="w-4 h-4 text-primary" />
                  Medical Coverage Requirements
                </h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="coverage_level">Level of Coverage Requested *</Label>
                    <Select value={formData.coverage_level} onValueChange={(v) => handleChange('coverage_level', v)}>
                      <SelectTrigger className={cn("bg-white", validationErrors.coverage_level && "border-red-300")}>
                        <SelectValue placeholder="Select coverage level" />
                      </SelectTrigger>
                      <SelectContent>
                        {coverageLevels.map(level => (<SelectItem key={level} value={level}>{level}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    {validationErrors.coverage_level && <p className="text-xs text-red-500">{validationErrors.coverage_level}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="additional_notes">Additional Notes / Special Risks</Label>
                    <Textarea 
                      id="additional_notes" 
                      value={formData.additional_notes} 
                      onChange={(e) => handleChange('additional_notes', e.target.value)} 
                      placeholder="Please mention any special risks: alcohol service, motorsport, water events, extreme heat, pyrotechnics, mosh pits, etc." 
                      rows={4} 
                      className="bg-white" 
                    />
                  </div>
                </div>
              </div>

              <Alert className="bg-slate-50 border-slate-200">
                <AlertDescription className="text-slate-600">{eventQuote.consentText}</AlertDescription>
              </Alert>

              <Button 
                type="submit" 
                size="lg" 
                className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300" 
                disabled={loading}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {eventQuote.submitButton}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card className="mt-6 border-0 shadow-lg bg-white">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4 text-slate-900">{eventQuote.needHelp}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="flex items-start gap-2 text-slate-600">
                <Phone className="w-4 h-4 text-primary mt-0.5" />
                <div className="flex flex-col">
                  {(contact.phones || [contact.phone]).map((num, i) => (
                    <a key={i} href={`tel:${num.replace(/[^+\d]/g, '')}`} className="hover:text-emerald-600 transition-colors">{num}</a>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Mail className="w-4 h-4 text-primary" />
                <span>{contact.eventsEmail}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
